import RSDK from '@sr/functions/rsdk/src';
import { Resume, Section } from '@sr/types';
import * as React from 'react';
import { Box, Button, EditIcon, Input, TrashIcon } from '../../ui';
import API from '../../utils/api';

type Props = {
  resume: Resume;
  sectionId: string;
  isSetup?: boolean | null;
  children: React.ReactNode;
};

const SectionFormContainer = ({
  resume,
  sectionId,
  isSetup,
  children,
}: Props) => {
  const section = resume.sections[sectionId];
  const inputEl = React.useRef<HTMLInputElement>(null);

  // We don't need any of this extra ui for the setup flow
  if (isSetup) {
    return <>{children}</>;
  }

  return (
    <Box
      id={`nav-${sectionId}`}
      className="gi-section-container"
      sx={{
        ':focus-within button.deleteSection': {
          opacity: 0.5,
        },
        ':hover button.deleteSection': {
          opacity: 0.5,
        },
        ':focus-within .editIcon': {
          opacity: 0.5,
        },
        ':hover .editIcon': {
          opacity: 0.5,
        },
      }}
    >
      <Box sx={{ display: 'flex' }}>
        <Box
          sx={{
            position: 'relative',
            flexGrow: 1,
            ':focus-within .editIcon': {
              opacity: '0 !important',
            },
          }}
        >
          <Input
            variant="inline"
            type="text"
            data-testid="section-title"
            value={section.title}
            onChange={(e) => renameSection(section, e.currentTarget.value)}
            onBlur={(e) => logRename(e.currentTarget.value, resume)}
            id={RSDK.getSectionPropSrId({
              sectionId,
              sectionType: section.type,
              contentType: section.contentType,
              prop: 'title',
            })}
            ref={inputEl}
          />
          <EditIcon
            className="editIcon gi-edit-section-name-icon"
            size={29}
            onClick={() => inputEl.current && inputEl.current.focus()}
            tabIndex={-1}
            sx={{
              position: 'absolute',
              top: '10px',
              right: 0,
              opacity: [1, 0],
              transition: 'opacity 150ms linear',
              cursor: 'pointer',
              ':hover': {
                opacity: '1 !important',
              },
            }}
          />
        </Box>
        <Button
          className="deleteSection gi-delete-section-button"
          tabIndex={-1}
          data-testid="delete-section"
          sx={{
            variant: 'buttons.link',
            opacity: [1, 0],
            transition: 'opacity 150ms linear',
            flexShrink: 0,
            flexGrow: 0,
            py: 3,
            ':hover': {
              opacity: '1 !important',
            },
          }}
          onClick={() => {
            if (
              window.confirm(
                'Are you sure that you want to delete this section?'
              )
            ) {
              deleteSection(sectionId, resume);
            }
          }}
        >
          <TrashIcon color="black" size={24} />
        </Button>
      </Box>
      {children}
    </Box>
  );
};

function deleteSection(sectionId: string, resume: Resume) {
  API.section.delete({
    resume,
    section: resume.sections[sectionId],
  });
  API.events.logEvent({
    userId: resume.owner,
    eventName: 'Resume Section Deleted',
  });
}

function renameSection(section: Section, name: string) {
  if (!name || name.length > 0) {
    API.section.update({
      section,
      updates: { title: name },
    });
  }
}

function logRename(name: string, resume: Resume) {
  API.events.logEvent({
    userId: resume.owner,
    eventName: 'Resume Section Renamed',
    properties: {
      Name: name,
    },
  });
}

export default SectionFormContainer;
